/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import {
  VENCIMENTO_CHART_HEIGHT,
  ChartEmpty,
  buildVencimentoColorMap,
  buildVencimentoTimelineModel,
  formatVencimentoYAxisTick,
  hasVencimentoChartData,
} from './chartShared';
import VencimentoAlertsTable from './VencimentoAlertsTable';
import VencimentoUrgencyKpis from './VencimentoUrgencyKpis';

const VencimentoTimelineChart = ({
  items,
  summary,
  window,
  alerts,
  loading,
  height,
  expanded,
  activeUrgency,
  onUrgencyChange,
  onAlertClick,
}) => {
  const theme = useTheme();
  const chartHeight = height || VENCIMENTO_CHART_HEIGHT;

  const { dataset, todayDateLabel } = useMemo(
    () => buildVencimentoTimelineModel(items, window),
    [items, window],
  );

  const colorMap = useMemo(
    () => buildVencimentoColorMap(dataset, theme),
    [dataset, theme],
  );

  const series = useMemo(
    () => [
      {
        dataKey: 'count',
        label: 'Vencimentos',
        valueFormatter: (value) => `${value ?? 0} NSIR${value === 1 ? '' : 's'}`,
      },
    ],
    [],
  );

  const hasData = hasVencimentoChartData(summary, items);
  const rotateLabels = expanded && dataset.length > 10;
  const foraDaJanela = summary?.foraDaJanela ?? 0;

  if (loading) {
    return <Skeleton variant="rounded" height={chartHeight + 160} />;
  }

  if (!hasData) {
    return <ChartEmpty message="Nenhuma NSIR com vencimento no período" />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <VencimentoUrgencyKpis
        summary={summary}
        activeUrgency={activeUrgency}
        onUrgencyChange={onUrgencyChange}
      />
      <BarChart
        dataset={dataset}
        series={series}
        xAxis={[
          {
            scaleType: 'band',
            dataKey: 'dateLabel',
            tickLabelStyle: {
              fontSize: 10,
              ...(rotateLabels ? { angle: -45, textAnchor: 'end' } : {}),
            },
            colorMap,
          },
        ]}
        yAxis={[
          {
            min: 0,
            valueFormatter: formatVencimentoYAxisTick,
          },
        ]}
        height={chartHeight}
        margin={{
          left: 36,
          right: 8,
          top: 8,
          bottom: rotateLabels ? 64 : 48,
        }}
        sx={{ width: '100%' }}
      >
        {todayDateLabel && (
          <ChartsReferenceLine
            x={todayDateLabel}
            label="Hoje"
            lineStyle={{
              stroke: theme.palette.warning.main,
              strokeDasharray: '4 4',
              strokeWidth: 2,
            }}
            labelStyle={{
              fontSize: 10,
              fontWeight: 700,
              fill: theme.palette.warning.dark,
            }}
          />
        )}
      </BarChart>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Janela operacional de {dataset.length} dias.
        {foraDaJanela > 0
          ? ` ${foraDaJanela.toLocaleString('pt-BR')} vencida(s) fora da janela (histórico).`
          : ' NSIRs não concluídas com vencimento por dia.'}
      </Typography>
      <VencimentoAlertsTable alerts={alerts} onAlertClick={onAlertClick} />
    </Box>
  );
};

VencimentoTimelineChart.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  summary: PropTypes.object,
  window: PropTypes.object,
  alerts: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  height: PropTypes.number,
  expanded: PropTypes.bool,
  activeUrgency: PropTypes.string,
  onUrgencyChange: PropTypes.func,
  onAlertClick: PropTypes.func,
};

VencimentoTimelineChart.defaultProps = {
  items: [],
  summary: null,
  window: null,
  alerts: [],
  loading: false,
  height: null,
  expanded: false,
  activeUrgency: null,
  onUrgencyChange: null,
  onAlertClick: null,
};

export default VencimentoTimelineChart;
